import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import React, { FC } from "react";
import { DeviceThumbnail } from "@/common/components/DeviceThumbnail/device-thumbnail";
import { formatCalendarDate } from "@/common/helpers/date-format.util";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { MyHardwareDevice } from "@/services/my-hardware/my-hardware.types";
import { AssetStatusChip } from "@/pages/MyHardware/components/AssetStatusChip/asset-status-chip";
import { actionForStatus, AssetActionKind } from "@/pages/MyHardware/my-hardware.types";

export type RowEmphasis = "primary" | "secondary" | "tertiary";

interface DeviceRowProps {
  device: MyHardwareDevice;
  hasDivider: boolean;
  emphasis: RowEmphasis;
  onAction: (device: MyHardwareDevice, action: AssetActionKind) => void;
}

const BUTTON_VARIANT: Record<RowEmphasis, ButtonVariant> = {
  primary: ButtonVariant.Primary,
  secondary: ButtonVariant.Secondary,
  tertiary: ButtonVariant.Tertiary,
};

const titleFor = (device: MyHardwareDevice): string => {
  if (!device.modelName && !device.serialNumber) return device.assetTag || device.assetId;
  if (!device.modelName) return device.serialNumber;
  if (!device.serialNumber) return device.modelName;
  return `${device.modelName} (${device.serialNumber})`;
};

const metaLineFor = (device: MyHardwareDevice): string => {
  const parts: string[] = [];
  if (device.assetTag) {
    parts.push(getString("myHardware.assetTag", { assetTag: device.assetTag }));
  }
  if (device.nextEligibleDate) {
    parts.push(
      getString("myHardware.nextEligible", { date: formatCalendarDate(device.nextEligibleDate) })
    );
  }
  return parts.join(" · ");
};

export const DeviceRow: FC<DeviceRowProps> = ({ device, hasDivider, emphasis, onAction }) => {
  const action = actionForStatus(device.status);
  const metaLine = metaLineFor(device);

  return (
    <Row
      width="100%"
      padding="S300"
      gridGap="S300"
      alignItems="center"
      style={hasDivider ? { borderTop: "1px solid var(--st-dt-color-neutral-20)" } : undefined}
      data-testid={`device-row-${device.assetTag || device.assetId}`}
    >
      <DeviceThumbnail altText={titleFor(device)} />

      <Col flex="1 1 auto" gridGap="S100" style={{ minWidth: 0 }}>
        <Row gridGap="S200" alignItems="center" flexWrap="wrap">
          <Text fontSize="T200" color="neutral90">
            {titleFor(device)}
          </Text>
          <AssetStatusChip status={device.status} />
        </Row>
        {metaLine && (
          <Text fontSize="T100" color="neutral70">
            {metaLine}
          </Text>
        )}
      </Col>

      {action && (
        <View flex="0 0 auto">
          <Button
            variant={BUTTON_VARIANT[emphasis]}
            onClick={() => onAction(device, action)}
            data-testid={`device-action-${device.assetTag || device.assetId}`}
          >
            {getString(`myHardware.assetAction.${action}`)}
          </Button>
        </View>
      )}
    </Row>
  );
};
